import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Sparkles } from "lucide-react";
import Layout from "@/components/Layout";
import Seo from "@/components/Seo";
import { type Bundle, type OrderMode } from "@/data/pricing";
import BundleShop from "@/components/BundleShop";
import BundleCustomizer from "@/components/BundleCustomizer";

type View = "shop" | "customize";

const PricingPage = () => {
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState<OrderMode>(
    searchParams.get("mode") === "subscription" ? "subscription" : "purchase",
  );
  const [view, setView] = useState<View>("shop");
  const [selectedBundle, setSelectedBundle] = useState<Bundle | null>(null);

  const handleSelectBundle = (bundle: Bundle) => {
    setSelectedBundle(bundle);
    setView("customize");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBack = () => {
    setView("shop");
    setSelectedBundle(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Layout>
      <Seo
        title="Beställ – Aquadias paket och prenumeration"
        description="Välj paket: från Droppen till Oceanen. Prenumerera på brustabletter och spara per dryck. Inga bindningstider."
        path="/pricing"
      />
      {/* Header */}
      <section className="bg-gradient-to-b from-coral-500/20 via-cream-100 to-cream-50 pt-20 md:pt-28 pb-6 px-6 text-center">
        <h1 className="text-2xl md:text-3xl font-black uppercase tracking-wider text-foreground mb-2">
          Beställ
        </h1>
        <p className="text-sm text-muted-foreground max-w-md mx-auto mb-4">
          Välj ett paket — anpassa sedan smakerna efter ditt behov.
        </p>

        <div className="flex justify-center gap-3">
          <button
            onClick={() => setMode("purchase")}
            aria-pressed={mode === "purchase"}
            className={`px-6 py-3 rounded-lg text-xs font-semibold uppercase tracking-widest transition-all duration-300 border-2 ${
              mode === "purchase"
                ? "bg-foreground text-background border-foreground shadow-lg"
                : "bg-white/50 text-foreground border-transparent hover:bg-white/70"
            }`}
          >
            KÖP
          </button>
          <button
            onClick={() => setMode("subscription")}
            aria-pressed={mode === "subscription"}
            className={`px-6 py-3 rounded-lg text-xs font-semibold uppercase tracking-widest transition-all duration-300 border-2 ${
              mode === "subscription"
                ? "bg-foreground text-background border-foreground shadow-lg"
                : "bg-white/50 text-foreground border-transparent hover:bg-white/70"
            }`}
          >
            PRENUMERERA
          </button>
        </div>

        <p className="text-xs text-foreground/80 mt-3 max-w-md mx-auto">
          {mode === "subscription"
            ? "Du startar en prenumeration — leverans och betalning varje månad, avsluta när du vill."
            : "Du gör ett engångsköp — en leverans, ingen månadsbetalning."}
        </p>


        <p className="text-[10px] text-muted-foreground uppercase tracking-widest mt-3">
          <Sparkles className="w-3 h-3 inline mr-1" />
          Dispenser ingår alltid i din beställning
        </p>
      </section>

      {/* Main content */}
      <section className={`px-6 md:px-12 ${view === "customize" ? "pb-32 md:pb-36" : "pb-16"}`}>
        <div className="max-w-6xl mx-auto pt-8">
          <div
            key={view}
            className="animate-in fade-in duration-300"
          >
            {view === "shop" && (
              <BundleShop mode={mode} onSelect={handleSelectBundle} />
            )}
            {view === "customize" && selectedBundle && (
              <div className="max-w-3xl mx-auto">
                <BundleCustomizer
                  bundle={selectedBundle}
                  mode={mode}
                  onBack={handleBack}
                />
              </div>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default PricingPage;
